import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewEncapsulation
} from '@angular/core';

import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { DealFacade } from '../../facades/deal.facade';
import { setupPopupDraggable } from '../../helpers/deals/popup-drag';
import { Globals } from '../../services/globals';
import { BidResult, BidResultsWithType } from '../../state/bid-results/state';
import { DealInformation } from '../../types/deal.types';
import { ManagerGridViewModel } from '../../types/deal.viewmodel.types';

export type BidResultNicTicKey = 'nic' | 'tic';

export interface BidResultsSeriesOption {
  id: string;
  label: string;
}

export interface BidResultsModalData {
  bidResults: BidResultsWithType;
  deal: ManagerGridViewModel | undefined;
  fromBidCompare: boolean;
  closeBidResultsModal: () => void;
  /** Security type: 'Bond' | 'Note'. Controls Par column visibility. */
  bondNoteType?: string;
  /** Series list from the deal, used to populate the series selector labels. */
  dealSeries?: Array<{ seriesId: number; name?: string | null }>;
}

@Component({
  templateUrl: './bid-results-modal.component.html',
  selector: 'muni-shared-bid-results-modal',
  styleUrls: ['./bid-results-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class BidResultsModalComponent
  implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    private dealFacade: DealFacade,
    private readonly elementRef: ElementRef,
    private readonly renderer: Renderer2,
    private globals: Globals
  ) { }

  private _context: BidResultsModalData;

  public get context(): BidResultsModalData {
    return this._context;
  }

  @Input()
  public set context(value: BidResultsModalData) {
    this._context = value;
    if (value) {
      this.changeDetectorRef.markForCheck();
    }
  }

  @Input()
  public response: Subject<any>;
  public data$: Observable<BidResultsModalData>;
  public bidResults$: Observable<BidResultsWithType>;
  public hasParColumn$: Observable<boolean>;
  public isMultiSeries$: Observable<boolean>;
  public displayedBids: BidResult[];
  public isNote: boolean;
  public altType: string;
  /** Lowercase key of the winning type: 'nic' or 'tic'. Used for dynamic field access. */
  public winningTypeKey: BidResultNicTicKey;
  public altTypeKey: BidResultNicTicKey;
  public seriesOptions: BidResultsSeriesOption[];
  public readonly selectedSeriesId$ = new BehaviorSubject<string>('combined');
  public eventListeners: (() => void)[] = [];

  public readonly winningOrionId$: Observable<number> = this.dealFacade.dealState$.pipe(
    filter((dealInfo: DealInformation) => dealInfo.loaded),
    map(
      (dealInfo) =>
        dealInfo.deal.seniorManager && dealInfo.deal.seniorManager.orionId
    )
  );

  ngAfterViewInit(): void {
    this.eventListeners = setupPopupDraggable(
      this.renderer,
      this.elementRef,
      'bid-results-modal-drag',
      true
    );
    setTimeout(() => {
      this.adjustBackdropSize();
    });
  }

  public ngOnInit() {
    const br = this.context.bidResults;

    this.isNote = this.context.bondNoteType === 'Note';
    this.altType = br.bidType === 'NIC' ? 'TIC' : 'NIC';
    this.winningTypeKey = br.bidType === 'NIC' ? 'nic' : 'tic';
    this.altTypeKey = br.bidType === 'NIC' ? 'tic' : 'nic';

    const firstBidWithSeries = br.bids.find((b) => b.series?.length > 1);
    const bidSeriesEntries = firstBidWithSeries?.series ?? [];

    this.seriesOptions = [
      { id: 'combined', label: 'Combined' },
      ...bidSeriesEntries.map((s) => {
        const dealSeriesMatch = this.context.dealSeries?.find(
          (ds) => String(ds.seriesId) === String(s.seriesId)
        );
        return {
          id: String(s.seriesId),
          label: dealSeriesMatch?.name || `Series ${s.seriesId}`
        };
      })
    ];

    this.data$ = of(this.context);
    this.bidResults$ = this.data$.pipe(map(({ bidResults }) => bidResults));

    // Multi-series when at least one bid carries more than one series entry
    this.isMultiSeries$ = of(bidSeriesEntries.length > 1);

    this.displayedBids = br.bids;

    this.hasParColumn$ = this.selectedSeriesId$.pipe(
      map((seriesId) => {
        if (!this.isNote || seriesId !== 'combined') {
          return false;
        }
        return br.bids.some(
          ({ parAmount }) => !isNaN(parAmount) && parAmount !== null
        );
      })
    );
  }

  public onSeriesChange(value: string): void {
    this.selectedSeriesId$.next(value);
  }

  /**
   * Returns the NIC or TIC value for a bid, either from the top-level nic/tic
   * (combined view) or from the matching series entry (series view).
   */
  public getSeriesValue(bid: BidResult, seriesId: string, key: BidResultNicTicKey): number | null {
    if (seriesId === 'combined') {
      return bid[key] ?? null;
    }
    const entry = bid.series?.find((s) => String(s.seriesId) === String(seriesId));
    return entry ? entry[key] : null;
  }

  public closeModal() {
    this.removeOverlay();
    this.response.complete();
  }

  private removeOverlay() {
    this.globals.nativeWindow.document.body.classList.remove(
      'bid-compare-overlay'
    );
  }

  @HostListener('window:resize')
  onResize() {
    this.adjustBackdropSize();
  }

  adjustBackdropSize() {
    if (!this._context.fromBidCompare) {
      return;
    }

    const container = this.globals.nativeWindow.document.querySelector(
      'muni-bidcomp-shared-compare'
    ) as HTMLElement;
    const backdrop = this.globals.nativeWindow.document.querySelector(
      '.bid-compare-overlay .c-modal-backdrop'
    ) as HTMLElement;
    const modalContainer = this.globals.nativeWindow.document.querySelector(
      '.compare-bid-results-modal'
    ) as HTMLElement;

    if (container && backdrop) {
      const { top, left, width, height } = container.getBoundingClientRect();

      // Position backdrop over the component
      backdrop.style.position = 'fixed';
      backdrop.style.top = `${top}px`;
      backdrop.style.left = `${left}px`;
      backdrop.style.width = `${width}px`;
      backdrop.style.height = `${height}px`;

      // set modal container styles
      const gap = 6;
      const modalWidth = modalContainer.offsetWidth;
      modalContainer.style.top = `${top + 6}px`;
      modalContainer.style.left = `${left + (width - modalWidth) / 2}px`;
      modalContainer.style.maxHeight = `${height - (gap * 2)}px`;
    }
  }

  public ngOnDestroy() {
    // Remove all event listeners
    this.eventListeners.forEach((eventListener) => eventListener());
    this.selectedSeriesId$.complete();
    this.removeOverlay();
  }
}
